import React from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { EditIcon, DeleteIcon } from '../components/icons';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

function assetUrl(relPath) {
  return relPath ? `${API_ORIGIN}/uploads/${relPath}` : null;
}

const EMPTY_FORM = { name: '', bank_details: '', logoFile: null, sealFile: null };

class Companies extends React.Component {
  state = {
    companies: [],
    loading: true,
    error: '',
    saving: false,
    showModal: false,
    editing: null,
    form: EMPTY_FORM,
    logoPreview: null,
    sealPreview: null
  };

  componentDidMount() {
    this.loadAll();
  }

  loadAll = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const res = await api.get('/companies');
      this.setState({ companies: res.data, loading: false });
    } catch (err) {
      this.setState({ error: 'Failed to load companies.', loading: false });
    }
  };

  openAddModal = () => {
    this.setState({ showModal: true, editing: null, form: EMPTY_FORM, logoPreview: null, sealPreview: null });
  };

  openEditModal = (company) => {
    this.setState({
      showModal: true,
      editing: company,
      form: { name: company.name || '', bank_details: company.bank_details || '', logoFile: null, sealFile: null },
      logoPreview: assetUrl(company.logo_path),
      sealPreview: assetUrl(company.seal_path)
    });
  };

  closeModal = () => this.setState({ showModal: false });

  handleNameChange = (e) => {
    this.setState({ form: { ...this.state.form, name: e.target.value }, error: '' });
  };

  handleTextChange = (e) => {
    this.setState({ form: { ...this.state.form, [e.target.name]: e.target.value } });
  };

  handleFileChange = (field, previewField) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    this.setState({
      form: { ...this.state.form, [field]: file },
      [previewField]: URL.createObjectURL(file)
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { editing, form } = this.state;

    const data = new FormData();
    data.append('name', form.name);
    data.append('bank_details', form.bank_details || '');
    if (form.logoFile) data.append('logo', form.logoFile);
    if (form.sealFile) data.append('seal', form.sealFile);

    this.setState({ saving: true, error: '' });
    try {
      if (editing) {
        await api.put(`/companies/${editing.id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.post('/companies', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      this.closeModal();
      this.loadAll();
    } catch (err) {
      const message = err.response?.data?.message || 'Save failed. Check the values and try again.';
      this.setState({ error: message });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm('Delete this company?')) return;
    try {
      await api.delete(`/companies/${id}`);
      this.loadAll();
    } catch (err) {
      this.setState({ error: 'Delete failed — it may be referenced by an invoice.' });
    }
  };

  render() {
    const { companies, loading, error, showModal, saving, editing, form, logoPreview, sealPreview } = this.state;

    return (
      <Layout>
        <div className="page-header">
          <div>
            <h1 className="page-title">Companies</h1>
            <p className="page-sub">Client companies billed on invoices</p>
          </div>
          <button className="btn btn-brass" onClick={this.openAddModal}>+ Add Company</button>
        </div>

        {error && !showModal && <div className="error-banner">{error}</div>}
        
        <div className="card">
          {loading ? (
            <p>Loading...</p>
          ) : companies.length === 0 ? (
            <div className="empty-state">
              <div className="glyph">—</div>
              No companies added yet.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>Name</th>
                  <th>Seal</th>
                  <th className="actions-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Logo">
                      {c.logo_path ? (
                        <img src={assetUrl(c.logo_path)} alt="" style={{ height: 32, borderRadius: 4 }} />
                      ) : '—'}
                    </td>
                    <td data-label="Name">{c.name}</td>
                    <td data-label="Seal">
                      {c.seal_path ? (
                        <img src={assetUrl(c.seal_path)} alt="" style={{ height: 32, borderRadius: 4 }} />
                      ) : '—'}
                    </td>
                    <td className="actions-cell" data-label="Action">
                      <div className="row-actions">
                        <button className="action-btn edit" title="Edit" onClick={() => this.openEditModal(c)}>
                          <EditIcon />
                        </button>
                        <button className="action-btn delete" title="Delete" onClick={() => this.handleDelete(c.id)}>
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="modal-backdrop" onClick={this.closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editing ? 'Edit Company' : 'Add Company'}</h2>

              {error && <div className="error-banner">{error}</div>}

              <form onSubmit={this.handleSubmit}>
                <div className="field">
                  <label>Company Name</label>
                  <input name="name" value={form.name} onChange={this.handleNameChange} required />
                </div>

                <div className="field">
                  <label>Logo</label>
                  <input type="file" accept="image/*" onChange={this.handleFileChange('logoFile', 'logoPreview')} />
                  {logoPreview && (
                    <img src={logoPreview} alt="Logo preview" style={{ height: 48, marginTop: 8, borderRadius: 4 }} />
                  )}
                </div>

                <div className="field">
                  <label>Company Seal</label>
                  <input type="file" accept="image/*" onChange={this.handleFileChange('sealFile', 'sealPreview')} />
                  {sealPreview && (
                    <img src={sealPreview} alt="Seal preview" style={{ height: 48, marginTop: 8, borderRadius: 4 }} />
                  )}
                </div>

                <div className="field">
                  <label>Bank Details</label>
                  <textarea
                    name="bank_details"
                    rows={3}
                    value={form.bank_details}
                    onChange={this.handleTextChange}
                    placeholder={'One bank per line: Bank Name & Branch | Account No | IFSC\ne.g. ICICI Bank Ltd - Malad Link Road II | 195905000213 | ICIC0001959'}
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-brass" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    );
  }
}

export default Companies;